import { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

interface DrinkButtonProps {
  icon: string;
  label: string;
  onDoubleTap: () => void;
  disabled?: boolean;
}

export function DrinkButton({ icon, label, onDoubleTap, disabled }: DrinkButtonProps) {
  const { t } = useLanguage();
  const [tapCount, setTapCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTap = () => {
    if (disabled) return;

    if (tapCount === 0) {
      setTapCount(1);
      timeoutRef.current = setTimeout(() => {
        setTapCount(0);
      }, 400);
    } else {
      // Double tap detected
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setTapCount(0);
      setIsAnimating(true);
      onDoubleTap();
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  return (
    <button
      onClick={handleTap}
      disabled={disabled}
      className={cn(
        "flex flex-col items-center justify-center",
        "w-full aspect-square max-w-32",
        "rounded-2xl bg-card border-2 border-border",
        "transition-all duration-200",
        "hover:border-primary/50 hover:shadow-md",
        "active:scale-95",
        tapCount === 1 && "border-primary/70 shadow-lg scale-[1.02]",
        isAnimating && "bg-primary/10 border-primary scale-110",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <span className="text-5xl mb-2">{icon}</span>
      <span className="text-sm font-medium text-foreground">{label}</span>
      {tapCount === 1 && (
        <span className="text-xs text-muted-foreground mt-1">{t('dashboardTapAgain')}</span>
      )}
    </button>
  );
}
